/**
 * Context-aware ingredient → supermarket department classifier.
 *
 * Approach (in order):
 *  1. **Rules**: regex-style patterns that match against the *raw* ingredient
 *     line (preserving qualifiers like "red", "bell", "ground", "black").
 *     Rules are evaluated top-to-bottom; first match wins. This is how we
 *     disambiguate "red pepper" (Produce) from "black pepper" (Spices) and
 *     "pepper" alone (which we treat as the spice — the more common usage).
 *  2. **Keyword fallback**: simple whole-word keyword scan against the
 *     normalised name (post-canonicalisation) for everything not covered by
 *     rules. Keeps the long tail working without writing a rule per item.
 *
 * Order in `DEPARTMENT_ORDER` defines display order in the shopping list.
 *
 * Misclassifications are a one-tap fix in the UI and learned per user, so
 * we don't need to be perfect — we just need to avoid the obviously wrong
 * groupings ("peppers" with "peppercorns") that erode trust.
 */

export const DEPARTMENT_ORDER = [
  "Produce",
  "Meat & Fish",
  "Dairy & Eggs",
  "Bakery",
  "Dry Goods",
  "Herbs & Spices",
  "Tins & Jars",
  "Oils, Sauces & Condiments",
  "Frozen",
  "Drinks",
  "Snacks",
  "Household / Other",
] as const;

export type Department = (typeof DEPARTMENT_ORDER)[number];

// ---------- Context-aware rules ----------
// Each rule: if the regex matches the raw lowercased name, classify as `dept`.
// Order matters — first match wins, so put more specific rules first.

type Rule = { pattern: RegExp; dept: Department; confidence: number };

// `\b` doesn't play nicely with hyphens but is good enough for our domain.
const RULES: Rule[] = [
  // ---- Produce: peppers (capsicum), explicitly named ----
  { pattern: /\b(red|green|yellow|orange|romano|sweet|bell|mini|baby)\s+peppers?\b/, dept: "Produce", confidence: 0.95 },
  { pattern: /\bbell\s+peppers?\b/, dept: "Produce", confidence: 0.98 },
  { pattern: /\b(jalape[nñ]o|serrano|habanero|scotch bonnet|padr[oó]n|poblano|anaheim)s?\b/, dept: "Produce", confidence: 0.95 },
  // Fresh chillies (without the word "powder" / "flakes" / "dried")
  { pattern: /\bfresh\s+(chill?ies?|chill?i)\b/, dept: "Produce", confidence: 0.95 },

  // ---- Herbs & Spices: peppercorns / ground / black / white pepper ----
  { pattern: /\b(black|white|pink|green)\s+pepper(corns?)?\b/, dept: "Herbs & Spices", confidence: 0.98 },
  { pattern: /\bpepper\s*corns?\b/, dept: "Herbs & Spices", confidence: 0.98 },
  { pattern: /\bground\s+pepper\b/, dept: "Herbs & Spices", confidence: 0.95 },
  { pattern: /\bcracked\s+pepper\b/, dept: "Herbs & Spices", confidence: 0.95 },
  // Bare "pepper" / "salt and pepper" → spice (the most common usage in recipes)
  { pattern: /\bsalt\s+(and|&|\+|,)\s+pepper\b/, dept: "Herbs & Spices", confidence: 0.95 },
  { pattern: /^pepper$/, dept: "Herbs & Spices", confidence: 0.85 },
  { pattern: /^pepper,/, dept: "Herbs & Spices", confidence: 0.85 },
  { pattern: /\b(chill?i|chili|cayenne|paprika)\s+(powder|flakes|seasoning)\b/, dept: "Herbs & Spices", confidence: 0.98 },
  { pattern: /\b(red|crushed)\s+pepper\s+flakes?\b/, dept: "Herbs & Spices", confidence: 0.98 },
  { pattern: /\bdried\s+(chill?ies?|chill?i)\b/, dept: "Herbs & Spices", confidence: 0.95 },

  // ---- Disambiguate "ground X" → meat vs spice ----
  // "ground beef/pork/lamb/turkey/chicken" → Meat
  { pattern: /\bground\s+(beef|pork|lamb|turkey|chicken|veal)\b/, dept: "Meat & Fish", confidence: 0.98 },
  // "ground X" with any other word → spice (cumin, coriander, ginger, cinnamon, allspice...)
  { pattern: /\bground\s+\w+\b/, dept: "Herbs & Spices", confidence: 0.85 },

  // ---- Tomatoes: fresh vs tinned ----
  { pattern: /\b(tin(ned)?|can(ned)?)\s+tomato/, dept: "Tins & Jars", confidence: 0.98 },
  { pattern: /\b(chopped|crushed|whole|plum|peeled)\s+tomatoes?\b/, dept: "Tins & Jars", confidence: 0.7 },
  { pattern: /\b(cherry|plum|vine|beef|baby)\s+tomatoes?\b/, dept: "Produce", confidence: 0.95 },
  { pattern: /\bsundried\s+tomato/, dept: "Tins & Jars", confidence: 0.95 },

  // ---- Onions: fresh vs powder ----
  { pattern: /\bonion\s+powder\b/, dept: "Herbs & Spices", confidence: 0.98 },
  { pattern: /\bgarlic\s+(powder|granules|salt)\b/, dept: "Herbs & Spices", confidence: 0.98 },
  { pattern: /\bgarlic\s+paste\b/, dept: "Oils, Sauces & Condiments", confidence: 0.85 },

  // ---- Ginger: fresh vs ground ----
  { pattern: /\bfresh\s+ginger\b/, dept: "Produce", confidence: 0.98 },
  { pattern: /\bginger\s+(powder|paste)\b/, dept: "Herbs & Spices", confidence: 0.95 },

  // ---- "X juice / zest" usually means buy the fruit ----
  { pattern: /\b(lemon|lime|orange|grapefruit)\s+(juice|zest|peel|rind)\b/, dept: "Produce", confidence: 0.85 },

  // ---- Coconut: milk (tin) vs flesh / cream / oil / desiccated ----
  { pattern: /\bcoconut\s+milk\b/, dept: "Tins & Jars", confidence: 0.95 },
  { pattern: /\bcoconut\s+cream\b/, dept: "Tins & Jars", confidence: 0.9 },
  { pattern: /\bcoconut\s+oil\b/, dept: "Oils, Sauces & Condiments", confidence: 0.95 },
  { pattern: /\bdesiccated\s+coconut\b/, dept: "Dry Goods", confidence: 0.95 },

  // ---- Stock cubes / pots ----
  { pattern: /\bstock\s+(cube|pot|powder)/, dept: "Tins & Jars", confidence: 0.9 },

  // ---- Beans: fresh vs tinned ----
  { pattern: /\b(green|french|runner|broad|fine)\s+beans?\b/, dept: "Produce", confidence: 0.95 },
  { pattern: /\b(black|kidney|cannellini|butter|borlotti|pinto|haricot)\s+beans?\b/, dept: "Tins & Jars", confidence: 0.85 },

  // ---- Cream: dairy ----
  { pattern: /\b(double|single|whipping|heavy|sour)\s+cream\b/, dept: "Dairy & Eggs", confidence: 0.98 },
  // (Cream cheese, ice cream handled by keyword fallback / earlier rules.)

  // ---- Frozen items, prefix-marked ----
  { pattern: /^frozen\s+/, dept: "Frozen", confidence: 0.95 },
];

// ---------- Keyword fallback ----------

const KEYWORDS: Record<Exclude<Department, "Household / Other">, string[]> = {
  Produce: [
    "onion", "shallot", "garlic", "leek", "spring onion", "scallion",
    "tomato", "potato", "sweet potato", "carrot", "celery", "cucumber",
    // Note: "pepper" intentionally NOT in this list — handled by rules.
    "bell pepper", "jalapeno", "jalapeño",
    "lettuce", "spinach", "kale", "rocket", "arugula", "cabbage", "broccoli",
    "cauliflower", "courgette", "zucchini", "aubergine", "eggplant",
    "mushroom", "ginger", "lime", "lemon", "orange", "apple", "banana",
    "berry", "berries", "strawberry", "blueberry", "raspberry", "grape",
    "avocado", "radish", "beetroot", "beet", "squash", "pumpkin",
    "corn", "asparagus", "peas", "fennel",
    "watercress", "salad", "herbs", "fruit", "vegetable",
  ],
  "Meat & Fish": [
    "chicken", "beef", "pork", "lamb", "turkey", "duck", "veal",
    "bacon", "sausage", "chorizo", "ham", "prosciutto", "salami",
    "mince", "steak", "ribs", "fillet",
    "salmon", "tuna", "cod", "haddock", "mackerel", "trout", "sea bass",
    "prawn", "shrimp", "scallop", "crab", "lobster", "anchovy", "anchovies",
    "fish", "seafood",
  ],
  "Dairy & Eggs": [
    "milk", "butter", "cheese", "cheddar", "parmesan", "feta", "mozzarella",
    "ricotta", "halloumi", "paneer", "tofu", "tempeh",
    "goat cheese", "blue cheese", "cream cheese",
    "yogurt", "yoghurt", "cream",
    "creme fraiche", "crème fraîche", "egg", "eggs",
  ],
  Bakery: [
    "bread", "loaf", "roll", "rolls", "bun", "baguette", "ciabatta",
    "pita", "tortilla", "wrap", "naan", "brioche", "croissant", "pastry",
    "puff pastry", "filo", "phyllo", "bagel", "muffin",
  ],
  "Dry Goods": [
    "flour", "sugar", "brown sugar", "icing sugar", "caster sugar",
    "rice", "basmati", "arborio", "risotto", "pasta", "spaghetti",
    "penne", "fusilli", "linguine", "tagliatelle", "lasagne", "noodle",
    "noodles", "couscous", "quinoa", "bulgur", "barley", "oats", "oatmeal",
    "polenta", "cornflour", "cornstarch", "baking powder", "baking soda",
    "yeast", "breadcrumbs", "panko", "lentil", "lentils", "chickpea",
    "split pea", "cocoa", "cacao", "chocolate chip", "vanilla extract",
    "almond", "cashew", "walnut", "pecan", "pine nut", "pistachio",
    "hazelnut", "peanut", "seed", "seeds", "sesame", "raisin", "raisins",
    "sultana", "currant", "dried fruit",
  ],
  "Herbs & Spices": [
    "basil", "parsley", "coriander", "cilantro", "mint", "thyme",
    "rosemary", "sage", "dill", "chives", "tarragon", "oregano", "bay",
    "bay leaf", "salt",
    "cumin", "paprika", "smoked paprika", "turmeric", "cinnamon",
    "nutmeg", "cardamom", "cloves", "allspice", "saffron", "curry powder",
    "garam masala", "cayenne",
    "mustard powder", "fennel seed",
    "coriander seed", "cumin seed", "star anise", "vanilla pod", "spice",
  ],
  "Tins & Jars": [
    "canned", "tinned", "tin of", "can of", "passata", "tomato puree",
    "tomato paste", "baked beans", "sweetcorn", "olives",
    "capers", "gherkin", "pickle", "artichoke",
    "stock", "broth", "bouillon", "jam", "marmalade", "honey",
    "peanut butter", "tahini", "nutella",
  ],
  "Oils, Sauces & Condiments": [
    "oil", "olive oil", "vegetable oil", "sunflower oil", "rapeseed oil",
    "sesame oil", "vinegar", "balsamic", "rice vinegar",
    "soy sauce", "fish sauce", "oyster sauce", "hoisin", "mirin",
    "worcestershire", "ketchup", "mayonnaise", "mayo", "mustard",
    "dijon", "wholegrain mustard", "sriracha", "tabasco", "hot sauce",
    "harissa", "pesto", "salsa", "bbq sauce", "marinade", "dressing",
    "syrup", "maple syrup", "golden syrup",
  ],
  Frozen: [
    "ice cream", "ice-cream", "ice lolly",
  ],
  Drinks: [
    "wine", "white wine", "red wine", "rosé", "rose wine", "beer",
    "cider", "champagne", "prosecco", "vermouth", "sherry", "port",
    "whisky", "whiskey", "vodka", "gin", "rum", "brandy", "tequila",
    "juice", "orange juice", "apple juice", "coffee", "tea",
    "water", "sparkling water", "tonic", "soda",
  ],
  Snacks: [
    "crisps", "tortilla chips", "popcorn", "biscuit", "cookie",
    "cracker", "pretzel", "nuts mix", "chocolate bar",
  ],
};

export type ClassifyResult = { department: Department; confidence: number };

/**
 * Classify an ingredient into a department.
 *
 * @param normalisedName  The canonicalised key (qualifiers stripped).
 *                        Used for the keyword fallback.
 * @param rawName         Optional original name (with qualifiers intact).
 *                        Used for context-aware rules. Falls back to
 *                        `normalisedName` when not provided.
 */
export const classifyDepartmentDetailed = (
  normalisedName: string,
  rawName?: string,
): ClassifyResult => {
  const raw = (rawName ?? normalisedName).toLowerCase().trim();

  // 1. Context-aware rules against raw name.
  for (const rule of RULES) {
    if (rule.pattern.test(raw)) {
      return { department: rule.dept, confidence: rule.confidence };
    }
  }

  // 2. Keyword fallback against normalised key.
  const padded = ` ${normalisedName.toLowerCase()} `;
  for (const dept of DEPARTMENT_ORDER) {
    if (dept === "Household / Other") continue;
    const list = KEYWORDS[dept as Exclude<Department, "Household / Other">];
    for (const kw of list) {
      if (padded.includes(` ${kw} `)) {
        return { department: dept, confidence: 0.6 };
      }
      if (padded.includes(` ${kw},`) || padded.includes(` ${kw}:`)) {
        return { department: dept, confidence: 0.6 };
      }
    }
  }
  return { department: "Household / Other", confidence: 0.3 };
};

/** Backwards-compatible classifier — returns just the department. */
export const classifyDepartment = (
  normalisedName: string,
  rawName?: string,
): Department => classifyDepartmentDetailed(normalisedName, rawName).department;
